import { useEffect, useState } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import Grid from '@zenbusiness/zui/Grid';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { generatePath } from 'react-router-dom';

import {
  CHECK_DEPOSIT_TYPES,
  IMAGE_TYPE
} from '#client/components/CheckDepositHistory/constants/types';
import { fetchImage } from '#client/components/CheckDepositHistory/utils/fetch';
import PaymentStatusLabel from '#client/components/PaymentStatusLabel';
import FieldDetail from '#client/components/TransactionDetailModal/components/FieldDetail';
import MoneyDetail from '#client/components/TransactionDetailModal/components/MoneyDetail';
import { DATE_FORMAT } from '#client/constants/date';
import { TOKEN_TYPES } from '#client/constants/tokens';
import { CHECK_DEPOSIT_STATUS, PROCESSING_STATUSES } from '#client/constants/transaction';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useCustomerToken from '#client/hooks/useCustomerToken';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { DEFAULT_SEGMENT } from '#client/manifest';

import CheckDepositImage from './CheckDepositImage';

export default function CheckDepositDetail({
  amount,
  createdAt,
  balanceAmount,
  type,
  estimatedCompletionDate,
  dateCompleted,
  id,
  status,
  reason,
  transactionId,
  isCheckDeposit
}) {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { unitAccountId } = useUnitAccountId();
  const { accessToken } = useAuthentication();
  const { getCustomerToken } = useCustomerToken({ type: TOKEN_TYPES.COMMON });
  const { createCommonErrorToast } = useToastContext();

  const [image, setImage] = useState({
    [IMAGE_TYPE.FRONT]: '',
    [IMAGE_TYPE.BACK]: ''
  });
  const [loading, setLoading] = useState(false);

  const saveImage = async ({ imageType, endpoint, customerToken }) => {
    const blob = await fetchImage({ endpoint, accessToken, customerToken });
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      setImage((preState) => ({
        ...preState,
        [imageType]: reader.result
      }));
    };
  };

  const getImage = async () => {
    if ((image[IMAGE_TYPE.FRONT] && image[IMAGE_TYPE.BACK]) || loading) {
      return;
    }

    setLoading(true);
    const customerToken = await getCustomerToken();
    if (!customerToken) {
      setLoading(false);
      return;
    }

    try {
      const frontImageEndpoint = generatePath(
        `${DEFAULT_SEGMENT}/check-deposits/:businessEntityUuid/:unitAccountId/:id/front`,
        { businessEntityUuid, unitAccountId, id }
      );
      const backImageEndpoint = generatePath(
        `${DEFAULT_SEGMENT}/check-deposits/:businessEntityUuid/:unitAccountId/:id/back`,
        { businessEntityUuid, unitAccountId, id }
      );

      const getFrontPromise = saveImage({
        endpoint: frontImageEndpoint,
        imageType: IMAGE_TYPE.FRONT,
        customerToken
      });
      const getBackPromise = saveImage({
        endpoint: backImageEndpoint,
        imageType: IMAGE_TYPE.BACK,
        customerToken
      });
      await Promise.all([getFrontPromise, getBackPromise]);
      setLoading(false);
    } catch (error) {
      createCommonErrorToast({ error });
      setLoading(false);
    }
  };

  useEffect(() => {
    getImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const typeText = CHECK_DEPOSIT_TYPES.includes(type) ? 'Check deposit' : type;
  const createdAtText = createdAt ? dayjs(createdAt).format(DATE_FORMAT.MOVE_MONEY) : '';
  const completedDateText = dateCompleted
    ? dayjs(dateCompleted).format(DATE_FORMAT.MOVE_MONEY)
    : '';
  const estimatedCompletionDateText = estimatedCompletionDate
    ? dayjs(estimatedCompletionDate).format(DATE_FORMAT.MOVE_MONEY)
    : '';
  const statusLabelText = PROCESSING_STATUSES.includes(status)
    ? CHECK_DEPOSIT_STATUS.PROCESSING
    : status;

  return (
    <Grid
      columnSpacing="8px"
      container
      pb={{ xs: '24px', md: '56px' }}
      pt={{ xs: '24px', md: '32px' }}
      px={{ xs: '24px', md: '32px' }}
      rowSpacing={{ xs: '16px', md: '32px' }}
    >
      <Grid
        item
        xs={12}
      >
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
        >
          <Text
            color="primary"
            size="xl"
            weight="medium"
          >
            Transaction details
          </Text>
        </Stack>
      </Grid>
      <Grid
        item
        xs={12}
      >
        <Grid container>
          <MoneyDetail
            label="Amount"
            value={amount}
          />
        </Grid>
      </Grid>
      <FieldDetail
        label="Date Initiated"
        value={createdAtText}
      />
      <FieldDetail
        label="Date Completed"
        value={completedDateText}
      />
      <FieldDetail
        label="Transaction Type"
        value={typeText}
      />
      <FieldDetail
        label="Account Balance"
        value={balanceAmount}
      />
      <FieldDetail
        label="Estimated Completion Date"
        value={estimatedCompletionDateText}
      />
      {isCheckDeposit && (
        <FieldDetail
          label="ID"
          value={id}
        />
      )}
      {!isCheckDeposit && (
        <FieldDetail
          label="Transaction ID"
          value={transactionId}
        />
      )}
      {status && (
        <Grid
          item
          md={4}
          xs={12}
        >
          <Stack
            direction="column"
            gap="8px"
          >
            <Text
              color="secondary"
              size="xs"
              uppercase
            >
              Status
            </Text>
            <PaymentStatusLabel status={statusLabelText} />
          </Stack>
        </Grid>
      )}
      <FieldDetail
        label="Reason"
        value={reason}
      />
      <CheckDepositImage
        backImage={image[IMAGE_TYPE.BACK]}
        frontImage={image[IMAGE_TYPE.FRONT]}
      />
    </Grid>
  );
}

CheckDepositDetail.propTypes = {
  amount: PropTypes.string,
  createdAt: PropTypes.string,
  type: PropTypes.string,
  estimatedCompletionDate: PropTypes.string,
  balanceAmount: PropTypes.string,
  id: PropTypes.string,
  status: PropTypes.string,
  reason: PropTypes.string,
  dateCompleted: PropTypes.string,
  transactionId: PropTypes.string,
  isCheckDeposit: PropTypes.bool
};
